import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NewRelicService } from './newrelic.service';

/**
 * Custom Angular ErrorHandler that reports unhandled errors to New Relic
 * Provides enhanced error context for debugging
 */
@Injectable()
export class NewRelicErrorHandler implements ErrorHandler {
  private newRelicService: NewRelicService;

  constructor(private injector: Injector) {}

  private getNewRelicService(): NewRelicService {
    if (!this.newRelicService) {
      this.newRelicService = this.injector.get(NewRelicService);
    }
    return this.newRelicService;
  }

  handleError(error: Error | HttpErrorResponse | unknown): void {
    const nrService = this.getNewRelicService();

    if (error instanceof HttpErrorResponse) {
      // HTTP errors
      const httpError = new Error(
        `HTTP Error: ${error.status} ${error.statusText} - ${error.url}`
      );
      httpError.name = 'HttpError';

      nrService.noticeError(httpError, {
        httpStatus: error.status,
        httpStatusText: error.statusText,
        httpUrl: error.url || 'unknown',
        errorType: 'http'
      });

      // Log to console in development
      console.error('HTTP Error:', error);
    } else if (error instanceof Error) {
      // JavaScript errors
      const attributes: Record<string, string | number | boolean> = {
        errorType: 'javascript',
        errorName: error.name
      };

      // Add component info if available (Angular specific)
      const errorRecord = error as unknown as Record<string, unknown>;
      if (errorRecord.ngDebugContext) {
        const context = errorRecord.ngDebugContext as {
          component?: { constructor: { name: string } };
        };
        if (context.component) {
          attributes.component = context.component.constructor.name;
        }
      }

      // Check for chunk loading errors (common in SPAs)
      if (error.message && error.message.includes('Loading chunk')) {
        attributes.errorType = 'chunk_load_error';
        attributes.chunkError = true;
      }

      nrService.noticeError(error, attributes);

      // Log to console in development
      console.error('JavaScript Error:', error);
    } else {
      // Unknown error type
      const unknownError = new Error(String(error));
      unknownError.name = 'UnknownError';

      nrService.noticeError(unknownError, {
        errorType: 'unknown',
        rawError: String(error)
      });

      console.error('Unknown Error:', error);
    }
  }
}
